import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Service.css';

const Service = () => {
  // Real-time Scroll Reveal Animation Hook
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('services-page-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const animatedElements = document.querySelectorAll(
      '.services-page-reveal, .services-page-reveal-left, .services-page-reveal-right, .services-page-reveal-scale'
    );
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const serviceCards = [
    {
      index: "01",
      title: "New Medical College Establishment",
      subtitle: "From Initial Planning to Institutional Readiness",
      desc: "Structured professional advisory covering preliminary project planning, proposed MBBS intake, hospital infrastructure design, academic manpower planning, and statutory readiness.",
      link: "/services/new-medical-college-establishment",
      badge: "Flagship Service",
      tags: ["Feasibility Study", "MBBS Intake Planning", "Hospital Infrastructure", "Readiness Roadmap"],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
        </svg>
      )
    },
    {
      index: "02",
      title: "MBBS Seat Expansion",
      subtitle: "Supporting the Next Stage of Institutional Growth",
      desc: "Comprehensive advisory, eligibility assessment, clinical material audit, and documentation support for existing Medical Colleges expanding to 150, 200, or 250 seats.",
      link: "/services/mbbs-seat-expansion",
      badge: "Academic Expansion",
      tags: ["Bed Occupancy Audit", "Faculty Gap Analysis", "Clinical Material Review", "Application Prep"],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      )
    },
    {
      index: "03",
      title: "PG (MD/MS) Course & Seat Expansion",
      subtitle: "Advancing Postgraduate Medical Education",
      desc: "Advisory and department-wise readiness support for introducing clinical and non-clinical MD/MS courses or increasing seat intake in existing postgraduate programmes.",
      link: "/services/pg-course-seat-expansion",
      badge: "Postgraduate Scope",
      tags: ["Departmental Readiness", "Senior Resident Mapping", "Unit Requirement Audits", "Documentation"],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3 3 9 3 12 0v-5"/>
        </svg>
      )
    },
    {
      index: "04",
      title: "Institutional & Teaching Hospital Compliance",
      subtitle: "Strengthening Academic & Clinical Preparedness",
      desc: "Systematic review of the integrated hospital and college ecosystem: ICU/OT preparedness, bed occupancy statistics, diagnostic labs, skill labs, and academic records.",
      link: "/services/institutional-compliance",
      badge: "Hospital & Clinical",
      tags: ["Clinical Material", "ICU & OT Statistics", "Diagnostic Services", "Skill Labs & Library"],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <line x1="12" y1="8" x2="12" y2="14"/>
          <line x1="9" y1="11" x2="15" y2="11"/>
        </svg>
      )
    },
    {
      index: "05",
      title: "Documentation & Institutional Readiness",
      subtitle: "Structured Documentation. Better Preparedness.",
      desc: "Systematic organisation, department-wise records preparation, hospital statistics compilation, gap identification, and post-assessment remediation support.",
      link: "/services/documentation-readiness",
      badge: "Statutory Readiness",
      tags: ["Record Auditing", "Faculty Credentials", "Hospital Data Verification", "Gap Rectification"],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      )
    },
    {
      index: "06",
      title: "Medical Faculty Recruitment",
      subtitle: "Building the Academic Strength of Medical Institutions",
      desc: "Turnkey recruitment of verified Permanent & Full-Time Medical Faculty across Pre-Clinical, Para-Clinical, and Clinical departments as per designation-wise institutional requirements.",
      link: "/services/medical-faculty-recruitment",
      badge: "Academic Manpower",
      tags: ["Professors & HODs", "Associate & Assistant Professors", "Senior Residents", "TEQ Eligibility"],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      )
    }
  ];

  const institutionalFramework = [
    { 
      step: "01", 
      phase: "ASSESS", 
      title: "Understand the Institution", 
      desc: "Initial requirement assessment, identifying existing capabilities, clinical infrastructure status, and institutional gaps.",
      deliverable: "Gap Assessment & Feasibility"
    },
    { 
      step: "02", 
      phase: "PLAN", 
      title: "Develop the Roadmap", 
      desc: "Structured planning across academic departments, Teaching Hospital bed strength, manpower, and documentation.",
      deliverable: "Strategic Master Blueprint"
    },
    { 
      step: "03", 
      phase: "PREPARE", 
      title: "Build Readiness", 
      desc: "Coordinating departmental readiness, clinical material statistics, and statutory documentation alignment.",
      deliverable: "Hospital & Lab Synchronization"
    },
    { 
      step: "04", 
      phase: "EXPAND", 
      title: "Support Academic Growth", 
      desc: "Targeted support for MBBS Seat Expansion, New PG (MD/MS) Courses, and postgraduate seat enhancement.",
      deliverable: "Intake Scaling & PG Addition"
    },
    { 
      step: "05", 
      phase: "STRENGTHEN", 
      title: "Address Institutional Gaps", 
      desc: "Readiness review, mock assessment checklists, gap remediation models, and corrective planning.",
      deliverable: "Audit Verification & Mock Drill"
    },
    { 
      step: "06", 
      phase: "SUPPORT", 
      title: "Build Academic Manpower", 
      desc: "Permanent & Full-Time Medical Faculty Recruitment with candidate-institution coordination.",
      deliverable: "Verified Faculty Deployment"
    }
  ];

  const whyNutrivioPillars = [
    {
      num: "01",
      tag: "Ecosystem Alignment",
      title: "Integrated Institutional Perspective",
      desc: "We view Medical College development as one integrated ecosystem involving academics, teaching hospital bed capacity, clinical material, and documentation.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      )
    },
    {
      num: "02",
      tag: "Full Lifecycle",
      title: "Establishment to Expansion Scope",
      desc: "Our framework guides institutions across all developmental lifecycle stages—from greenfield planning through undergraduate and postgraduate expansion.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      )
    },
    {
      num: "03",
      tag: "Dual Advantage",
      title: "Advisory + Faculty Capability",
      desc: "We combine strategic institutional consulting with verified Permanent & Full-Time Medical Faculty staffing under one roof.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      )
    },
    {
      num: "04",
      tag: "Methodical Model",
      title: "Structured Professional Framework",
      desc: "Every advisory engagement follows milestone-driven gap assessments, verifiable checklists, and transparent execution timelines.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      )
    },
    {
      num: "05",
      tag: "Tailored Strategy",
      title: "Institution-Specific Engagement",
      desc: "Engagements are customized strictly around the promoter's vision, target intake, proposed bed capacity, and agreed project scope.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      )
    },
    {
      num: "06",
      tag: "Strict NDA Ethics",
      title: "Confidential & Ethical Protocol",
      desc: "Institutional data, promoter strategies, and project timelines are protected under non-disclosure protocols.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services | Medical College Advisory & Institutional Solutions</title>
        <meta
          name="description"
          content="Explore Nutrivio's institutional services: New Medical College Establishment, MBBS Seat Expansion, PG MD/MS Courses, Hospital Compliance, and Medical Faculty Recruitment."
        />
      </Helmet>

      <div className="services-page-wrapper">
        
        {/* Ambient Dot Matrix */}
        <div className="services-page-ambient-pattern"></div>

        {/* ================= 1. BREADCRUMB & BIG "OUR SERVICES" HEADER ================= */}
        <section className="services-page-hero">
          <div className="services-page-container">
            <div className="services-page-hero-header services-page-reveal">
              
              {/* Breadcrumb Pill */}
              <div className="services-page-breadcrumb">
                <Link to="/" className="services-page-breadcrumb-link">Home</Link>
                <span className="services-page-breadcrumb-sep">/</span>
                <span className="services-page-breadcrumb-current">Services</span>
              </div>

              {/* Big "Our Services" Title */}
              <h1 className="services-page-main-title">
                Our <span className="services-page-gradient-text">Services</span>
              </h1>

              {/* Minimal Accent Dash */}
              <div className="services-page-heading-dash"></div>

            </div>
          </div>
        </section>

        {/* ================= 2. ALL 6 SERVICE CARDS GRID ================= */}
        <section className="services-page-grid-section">
          <div className="services-page-container">
            
            <div className="services-page-section-header services-page-reveal">
              <div className="services-page-section-eyebrow center">
                <span className="services-page-eyebrow-line"></span>
                <span>Tailored Institutional Solutions</span>
                <span className="services-page-eyebrow-line"></span>
              </div>
              <h2 className="services-page-section-title single-line-title">
                What We Provide &amp; Deliver
              </h2>
              <p className="services-page-section-subtitle full-width-desc">
                Select any service module below to explore its specific scope of work, execution deliverables, and institutional impact.
              </p>
            </div>

            <div className="services-page-cards-grid">
              {serviceCards.map((service, index) => (
                <div key={index} className="services-page-card services-page-reveal">
                  
                  {/* Top Glowing Accent Line */}
                  <div className="services-page-card-hover-line"></div>

                  {/* Watermark Index Number */}
                  <span className="services-page-card-watermark">{service.index}</span>

                  {/* Top Bar: Icon + Badge */}
                  <div className="services-page-card-top">
                    <div className="services-page-card-icon-box">
                      {service.icon}
                    </div>
                    <span className="services-page-card-badge">{service.badge}</span>
                  </div>

                  <h3 className="services-page-card-title">{service.title}</h3>
                  <h6 className="services-page-card-subtitle">{service.subtitle}</h6>
                  <p className="services-page-card-desc">{service.desc}</p>

                  {/* Key Scope Tags */}
                  <div className="services-page-card-tags">
                    {service.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="services-page-tag">{tag}</span>
                    ))}
                  </div>

                  {/* View Service Button */}
                  <div className="services-page-card-footer">
                    <Link to={service.link} className="services-page-card-btn">
                      <span>View Service Details</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Link>
                  </div>

                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ================= 3. 6-STEP INSTITUTIONAL FRAMEWORK ================= */}
        <section className="services-page-framework-section">
          <div className="services-page-container">
            
            <div className="services-page-section-header services-page-reveal">
              <div className="services-page-section-eyebrow center">
                <span className="services-page-eyebrow-line"></span>
                <span>Lifecycle Delivery Protocol</span>
                <span className="services-page-eyebrow-line"></span>
              </div>
              <h2 className="services-page-section-title single-line-title">
                The Nutrivio Institutional Framework
              </h2>
              <p className="services-page-section-subtitle full-width-desc">
                A structured six-stage execution roadmap bridging initial assessment, hospital synchronization, and operational statutory readiness.
              </p>
            </div>

            <div className="services-page-framework-grid">
              {institutionalFramework.map((item, index) => (
                <div key={index} className="services-page-framework-card services-page-reveal">
                  <div className="services-page-framework-glow-line"></div>

                  <div className="services-page-framework-node-top">
                    <div className="services-page-framework-circle">
                      <span>{item.step}</span>
                    </div>
                    <span className="services-page-framework-phase">{item.phase}</span>
                  </div>

                  <h4 className="services-page-framework-title">{item.title}</h4>
                  <p className="services-page-framework-desc">{item.desc}</p>

                  <div className="services-page-framework-footer">
                    <span className="services-page-framework-check">✓</span>
                    <span>{item.deliverable}</span>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ================= 4. WHY NUTRIVIO (STRATEGIC PILLARS) ================= */}
        <section className="services-page-why-section">
          <div className="services-page-container">
            
            <div className="services-page-section-header services-page-reveal">
              <div className="services-page-section-eyebrow center">
                <span className="services-page-eyebrow-line"></span>
                <span>Strategic Advantage</span>
                <span className="services-page-eyebrow-line"></span>
              </div>
              <h2 className="services-page-section-title single-line-title">
                Why Medical Institutions Partner with Nutrivio
              </h2>
              <p className="services-page-section-subtitle full-width-desc">
                Professional excellence, deep statutory understanding, and integrated healthcare manpower capabilities across all milestones.
              </p>
            </div>

            <div className="services-page-why-grid">
              {whyNutrivioPillars.map((pillar, index) => (
                <div key={index} className="services-page-why-card services-page-reveal">
                  <div className="services-page-why-hover-line"></div>

                  <div className="services-page-why-card-top">
                    <div className="services-page-why-icon-box">
                      {pillar.icon}
                    </div>
                    <span className="services-page-why-num">{pillar.num}</span>
                  </div>

                  <span className="services-page-why-tag">{pillar.tag}</span>
                  <h4 className="services-page-why-title">{pillar.title}</h4>
                  <p className="services-page-why-desc">{pillar.desc}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ================= 5. COMPACT EXECUTIVE CTA SECTION ================= */}
        <section className="services-page-cta-section services-page-reveal">
          <div className="services-page-container">
            
            <div className="services-page-cta-compact-card">
              <div className="services-page-cta-ambient-glow"></div>

              <div className="services-page-cta-pill">
                <span className="services-page-cta-dot"></span>
                <span>Ready to Advance Your Medical Institution?</span>
              </div>

              <h2 className="services-page-cta-title single-line-title">
                Planning the Future of a <span className="services-page-cta-gradient-text">Medical Education Institution?</span>
              </h2>

              <p className="services-page-cta-desc full-width-desc">
                Whether establishing a New Medical College, expanding MBBS/PG seats, or recruiting verified full-time faculty, Nutrivio provides structured advisory aligned with your institutional milestones.
              </p>

              <div className="services-page-cta-btn-row">
                <Link to="/for-institutions" className="services-page-cta-btn-primary">
                  <span>Discuss Your Project</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>

                <Link to="/contact" className="services-page-cta-btn-secondary">
                  <span>Contact Advisory Desk</span>
                </Link>
              </div>

              <div className="services-page-brand-closing">
                <div className="services-page-closing-motto">From Vision to Institution. From Establishment to Expansion.</div>
                <div className="services-page-closing-company">NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED</div>
                <div className="services-page-closing-sub">Strategic Professional Support for Medical Education Institutions across India</div>
              </div>

            </div>

          </div>
        </section>

      </div>
    </>
  );
};

export default Service;